import React, { useState, useMemo, useEffect, useRef } from "react";
import Nav from "./Navigation.js";
import {
  GRID_SIZE, KEY_ROW, buildGrid, applyMove,
  solvePuzzle, formatMove, getPieceColor, PIECE_COLORS,
} from "./RushHourUtils.js";

const CELL = 56;
const GAP = 2;
const BOARD_PX = GRID_SIZE * CELL + (GRID_SIZE - 1) * GAP;
const BLOCK_IDS = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T'];

function pW(o, len) { return o === 'h' ? len * CELL + (len - 1) * GAP : CELL; }
function pH(o, len) { return o === 'v' ? len * CELL + (len - 1) * GAP : CELL; }

function checkDrop(r, c, cfg, curPieces) {
  if (cfg.isKey && curPieces.some(p => p.isKey)) return false;
  if (!cfg.isKey) {
    const used = new Set(curPieces.map(p => p.id));
    if (!BLOCK_IDS.some(id => !used.has(id))) return false;
  }
  if (cfg.orientation === 'h' && c + cfg.length > GRID_SIZE) return false;
  if (cfg.orientation === 'v' && r + cfg.length > GRID_SIZE) return false;
  const grid = buildGrid(curPieces);
  for (let i = 0; i < cfg.length; i++) {
    const pr = cfg.orientation === 'v' ? r + i : r;
    const pc = cfg.orientation === 'h' ? c + i : c;
    if (pr >= GRID_SIZE || pc >= GRID_SIZE) return false;
    if (grid[pr][pc] !== null) return false;
  }
  return true;
}

export default function RushHourSolverPage({ isModal = false, onClose } = {}) {
  const [pieces, setPieces] = useState([]);
  const [cfg, setCfg] = useState({ orientation: 'h', length: 2, isKey: false, locked: false });
  const [solution, setSolution] = useState(null);
  const [solStep, setSolStep] = useState(0);
  const [showSol, setShowSol] = useState(false);
  const [error, setError] = useState(null);
  const [drag, setDrag] = useState(null); // { x, y, hover: {r,c}|null } | null

  const boardRef = useRef(null);
  const piecesRef = useRef(pieces);
  useEffect(() => { piecesRef.current = pieces; }, [pieces]);
  const cfgRef = useRef(cfg);
  useEffect(() => { cfgRef.current = cfg; }, [cfg]);
  const dragRef = useRef(null);

  const nextId = useMemo(() => {
    if (cfg.isKey) return 'key';
    const used = new Set(pieces.map(p => p.id));
    for (const id of BLOCK_IDS) if (!used.has(id)) return id;
    return null;
  }, [pieces, cfg.isKey]);

  function getBoardCell(x, y) {
    if (!boardRef.current) return null;
    const rect = boardRef.current.getBoundingClientRect();
    const c = Math.floor((x - rect.left) / (CELL + GAP));
    const r = Math.floor((y - rect.top) / (CELL + GAP));
    if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE) return null;
    return { r, c };
  }

  // Global drag handlers — mounted once, use refs to read current state
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      e.preventDefault();
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const hover = getBoardCell(x, y);
      const next = { x, y, hover };
      dragRef.current = next;
      setDrag({ ...next });
    };
    const onUp = () => {
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      setDrag(null);
      if (!d.hover) return;
      const { r, c } = d.hover;
      const cur = cfgRef.current;
      const cp = piecesRef.current;
      if (!checkDrop(r, c, cur, cp)) return;
      const id = cur.isKey ? 'key' : (() => {
        const used = new Set(cp.map(p => p.id));
        for (const bid of BLOCK_IDS) if (!used.has(bid)) return bid;
        return null;
      })();
      if (!id) return;
      setPieces(prev => [...prev, { id, row: r, col: c, length: cur.length, orientation: cur.orientation, locked: cur.locked, isKey: cur.isKey }]);
      setSolution(null);
      setError(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startDrag = (x, y) => {
    const d = { x, y, hover: null };
    dragRef.current = d;
    setDrag(d);
  };

  const removePiece = (id) => {
    if (showSol) return;
    setPieces(prev => prev.filter(p => p.id !== id));
    setSolution(null);
    setError(null);
  };

  const handleSolve = () => {
    if (!pieces.some(p => p.isKey)) {
      setError("Place the Key piece first — enable 'Key', then drag it to row 3.");
      return;
    }
    setError(null);
    const sol = solvePuzzle(pieces);
    if (!sol) {
      setError("No solution found — make sure the board matches your game exactly.");
      setSolution(null); setShowSol(false);
      return;
    }
    if (sol.length === 0) {
      setError("The key can already exit! Slide it right.");
      return;
    }
    setSolution(sol); setSolStep(0); setShowSol(true);
  };

  const displayPieces = useMemo(() => {
    if (!showSol || !solution) return pieces;
    let p = pieces.map(x => ({ ...x }));
    for (let i = 0; i < solStep; i++) p = applyMove(p, solution[i]);
    return p;
  }, [showSol, solution, solStep, pieces]);

  const pieceColor = cfg.isKey ? PIECE_COLORS.key : (PIECE_COLORS[nextId] || '#6b7280');
  const hoverValid = drag?.hover ? checkDrop(drag.hover.r, drag.hover.c, cfg, pieces) : false;

  return (
    <div className="text-white">
      {!isModal && <Nav />}
      <div className={isModal ? "px-2 py-1" : "container mx-auto my-6 px-3"}>
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl lg:text-3xl font-bold text-center flex-1">Key Puzzle Solver</h1>
          {isModal && onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none ml-2" aria-label="Close">✕</button>
          )}
        </div>
        <p className="text-gray-400 mb-5 text-sm text-center">
          Build the board to match your game, then hit Solve. Tap any piece to remove it.
        </p>

        <div className="flex flex-col items-center gap-4 lg:flex-row lg:items-start lg:justify-center">

          {/* Left: configurator + board */}
          <div className="flex flex-col items-center gap-3">

            {/* Piece configurator */}
            {!showSol && (
              <div className="bg-gray-800 rounded-xl p-3 shadow" style={{ width: BOARD_PX + 32 }}>
                {/* Controls row */}
                <div className="flex flex-wrap gap-x-3 gap-y-2 items-center mb-3">
                  {/* Key toggle */}
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input type="checkbox" checked={cfg.isKey}
                      onChange={e => setCfg(a => ({ ...a, isKey: e.target.checked, locked: false }))}
                      className="w-4 h-4 accent-red-500" />
                    <span className="text-sm font-medium" style={{ color: cfg.isKey ? '#f87171' : '#9ca3af' }}>Key</span>
                  </label>

                  <span className="text-gray-600 hidden sm:inline">|</span>

                  {/* Orientation */}
                  <div className="flex gap-1">
                    {['h', 'v'].map(o => (
                      <button key={o} onClick={() => setCfg(a => ({ ...a, orientation: o }))}
                        className={`px-3 py-1 text-sm rounded transition-colors ${cfg.orientation === o ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
                        {o === 'h' ? '↔ Horiz' : '↕ Vert'}
                      </button>
                    ))}
                  </div>

                  <span className="text-gray-600 hidden sm:inline">|</span>

                  {/* Length */}
                  <div className="flex gap-1">
                    {[1, 2, 3].map(l => (
                      <button key={l} onClick={() => setCfg(a => ({ ...a, length: l }))}
                        className={`w-9 py-1 text-sm rounded transition-colors ${cfg.length === l ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}>
                        {l}
                      </button>
                    ))}
                  </div>

                  {/* Locked */}
                  {!cfg.isKey && (
                    <>
                      <span className="text-gray-600 hidden sm:inline">|</span>
                      <label className="flex items-center gap-1.5 cursor-pointer select-none">
                        <input type="checkbox" checked={cfg.locked}
                          onChange={e => setCfg(a => ({ ...a, locked: e.target.checked }))}
                          className="w-4 h-4" />
                        <span className="text-sm text-gray-400">🔒 Locked</span>
                      </label>
                    </>
                  )}
                </div>

                {/* Drag source */}
                <div className="flex items-center gap-3 bg-gray-700 rounded-lg px-3 py-2">
                  <span className="text-xs text-gray-400 shrink-0">Drag onto board →</span>
                  <div
                    onMouseDown={e => { e.preventDefault(); startDrag(e.clientX, e.clientY); }}
                    onTouchStart={e => { e.preventDefault(); startDrag(e.touches[0].clientX, e.touches[0].clientY); }}
                    style={{
                      width: pW(cfg.orientation, cfg.length),
                      height: pH(cfg.orientation, cfg.length),
                      backgroundColor: cfg.locked ? '#4b5563' : pieceColor,
                      borderRadius: 6,
                      border: '2px solid rgba(255,255,255,0.35)',
                      cursor: 'grab',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      userSelect: 'none', touchAction: 'none', flexShrink: 0,
                    }}
                  >
                    {cfg.isKey && <span style={{ color: 'white', fontSize: 8, fontWeight: 'bold', letterSpacing: 1 }}>KEY</span>}
                    {cfg.locked && !cfg.isKey && <span style={{ fontSize: 11 }}>🔒</span>}
                  </div>
                  <span className="text-xs text-gray-500">
                    {cfg.isKey ? 'Key' : `Block ${nextId ?? '—'}`}
                    {' · '}
                    {cfg.length === 1 ? '1×1' : cfg.orientation === 'h' ? `${cfg.length}×1` : `1×${cfg.length}`}
                    {cfg.locked ? ' · Locked' : ''}
                  </span>
                </div>
              </div>
            )}

            {/* Board */}
            <div className="bg-gray-800 rounded-xl p-4 shadow-lg">
              <div className="flex justify-between items-center mb-3">
                <span className="text-gray-400 text-sm">
                  {showSol
                    ? `Step ${solStep} / ${solution?.length}`
                    : `${pieces.length} piece${pieces.length !== 1 ? 's' : ''} placed`}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setPieces([]); setSolution(null); setShowSol(false); setError(null); }}
                    className="px-3 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 transition-colors">
                    Clear All
                  </button>
                  {showSol && (
                    <button onClick={() => { setShowSol(false); setSolution(null); }}
                      className="px-3 py-1 text-xs rounded bg-gray-700 hover:bg-gray-600 transition-colors">
                      Edit Board
                    </button>
                  )}
                </div>
              </div>

              <div ref={boardRef} style={{ position: 'relative', width: BOARD_PX, height: BOARD_PX }}>
                {/* Background grid cells */}
                {Array.from({ length: GRID_SIZE }, (_, r) =>
                  Array.from({ length: GRID_SIZE }, (_, c) => (
                    <div key={`bg-${r}-${c}`}
                      onClick={() => {
                        if (showSol) return;
                        const id = buildGrid(pieces)[r]?.[c];
                        if (id) removePiece(id);
                      }}
                      style={{
                        position: 'absolute',
                        top: r * (CELL + GAP), left: c * (CELL + GAP),
                        width: CELL, height: CELL,
                        backgroundColor: r === KEY_ROW ? '#1e3a5f' : '#374151',
                        borderRadius: 3,
                        cursor: showSol ? 'default' : 'pointer',
                      }}
                    />
                  ))
                )}

                {/* Drop ghost — snapped to grid */}
                {drag?.hover && (
                  <div style={{
                    position: 'absolute',
                    top: drag.hover.r * (CELL + GAP),
                    left: drag.hover.c * (CELL + GAP),
                    width: pW(cfg.orientation, cfg.length),
                    height: pH(cfg.orientation, cfg.length),
                    backgroundColor: hoverValid ? (cfg.locked ? '#4b5563' : pieceColor) : '#dc2626',
                    opacity: hoverValid ? 0.55 : 0.3,
                    borderRadius: 6,
                    border: `2px solid ${hoverValid ? 'rgba(255,255,255,0.55)' : 'rgba(220,38,38,0.7)'}`,
                    pointerEvents: 'none',
                    zIndex: 5,
                  }} />
                )}

                {/* Exit arrow */}
                <div style={{
                  position: 'absolute',
                  top: KEY_ROW * (CELL + GAP) + CELL / 2 - 10,
                  left: BOARD_PX + 6,
                  color: '#22c55e', fontSize: 20, fontWeight: 'bold', pointerEvents: 'none',
                }}>→</div>

                {/* Placed pieces */}
                {displayPieces.map(piece => (
                  <div key={piece.id}
                    onClick={() => removePiece(piece.id)}
                    style={{
                      position: 'absolute',
                      top: piece.row * (CELL + GAP), left: piece.col * (CELL + GAP),
                      width: pW(piece.orientation, piece.length),
                      height: pH(piece.orientation, piece.length),
                      backgroundColor: getPieceColor(piece),
                      borderRadius: 6,
                      border: '2px solid rgba(255,255,255,0.2)',
                      zIndex: 2,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: showSol ? 'default' : 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    {piece.isKey && <span style={{ color: 'white', fontWeight: 'bold', fontSize: 10, letterSpacing: 1 }}>KEY</span>}
                    {!piece.isKey && <span style={{ color: 'rgba(255,255,255,0.75)', fontWeight: 'bold', fontSize: 12 }}>{piece.id}</span>}
                    {piece.locked && <span style={{ fontSize: 11, marginLeft: 2 }}>🔒</span>}
                  </div>
                ))}
              </div>

              {error && (
                <div className="mt-3 text-red-400 text-sm bg-red-900 bg-opacity-30 rounded px-3 py-2">{error}</div>
              )}

              {!showSol && (
                <button onClick={handleSolve}
                  className="mt-3 w-full py-2 rounded bg-green-600 hover:bg-green-500 text-white text-sm font-semibold transition-colors">
                  Solve Puzzle
                </button>
              )}
            </div>
          </div>

          {/* Right: solution panel + tips */}
          <div className="flex flex-col gap-3 w-60">
            {showSol && solution && solution.length > 0 && (
              <div className="bg-gray-800 rounded-xl p-4 text-left">
                <p className="text-sm font-semibold mb-3">Solution — {solution.length} moves</p>
                <ol className="text-sm space-y-1 mb-4 max-h-96 overflow-y-auto pr-1">
                  {solution.map((move, i) => (
                    <li key={i} onClick={() => setSolStep(i)}
                      className={`px-2 py-1.5 rounded cursor-pointer transition-colors ${
                        i === solStep ? 'bg-blue-700 text-white'
                        : i < solStep ? 'text-gray-500'
                        : 'text-gray-300 hover:bg-gray-700'
                      }`}>
                      <span className="font-medium mr-1">{i + 1}.</span>
                      {formatMove(move, pieces)}
                    </li>
                  ))}
                  <li onClick={() => setSolStep(solution.length)}
                    className={`px-2 py-1.5 rounded cursor-pointer text-green-400 font-medium transition-colors ${
                      solStep === solution.length ? 'bg-green-900 bg-opacity-40' : 'hover:bg-gray-700'
                    }`}>
                    ✓ Key exits!
                  </li>
                </ol>
                <div className="flex gap-2">
                  <button onClick={() => setSolStep(s => Math.max(0, s - 1))} disabled={solStep === 0}
                    className="flex-1 py-1.5 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 transition-colors">
                    ← Prev
                  </button>
                  <button onClick={() => setSolStep(s => Math.min(solution.length, s + 1))} disabled={solStep === solution.length}
                    className="flex-1 py-1.5 text-xs rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-40 transition-colors">
                    Next →
                  </button>
                </div>
              </div>
            )}

            <div className="bg-gray-800 rounded-xl p-3 text-xs text-gray-400 text-left">
              <p className="font-semibold text-gray-300 mb-2">How to use</p>
              <ul className="space-y-1.5 list-disc list-inside">
                <li>Pick <strong className="text-gray-300">Key</strong>, orientation, and length</li>
                <li>Drag the piece from the bar onto the board</li>
                <li>Ghost turns <span className="text-green-400">green</span> when placement is valid</li>
                <li>Tap any piece to remove it</li>
                <li>Blue row = Key exit row (row 3)</li>
                <li>Hit <strong className="text-gray-300">Solve</strong> once the board matches</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Floating ghost that follows the cursor / finger */}
      {drag && (
        <div style={{
          position: 'fixed',
          left: drag.x - pW(cfg.orientation, cfg.length) / 2,
          top: drag.y - pH(cfg.orientation, cfg.length) / 2,
          width: pW(cfg.orientation, cfg.length),
          height: pH(cfg.orientation, cfg.length),
          backgroundColor: cfg.locked ? '#4b5563' : pieceColor,
          borderRadius: 6,
          border: '2px solid rgba(255,255,255,0.5)',
          opacity: 0.85,
          pointerEvents: 'none',
          zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {cfg.isKey && <span style={{ color: 'white', fontSize: 8, fontWeight: 'bold', letterSpacing: 1 }}>KEY</span>}
          {cfg.locked && !cfg.isKey && <span style={{ fontSize: 11 }}>🔒</span>}
        </div>
      )}
    </div>
  );
}
